import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Eye, Library } from 'lucide-react';
import { AdaptiveModal } from '@/components/ui/AdaptiveModal';
import { Button } from '@/components/ui/button';
import { useHaptics } from '@/hooks/useHaptics';

interface CompositionSuccessDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  compositionTitle: string;
  compositionId: string;
  isEditing?: boolean;
}

const CompositionSuccessDialog: React.FC<CompositionSuccessDialogProps> = ({
  isOpen,
  onOpenChange,
  compositionTitle,
  compositionId,
  isEditing = false
}) => {
  const navigate = useNavigate();
  const { successNotification } = useHaptics();

  const handleViewComposition = () => {
    successNotification();
    onOpenChange(false);
    navigate(`/composition/${compositionId}`);
  };

  const handleBrowseCompositions = () => {
    onOpenChange(false);
    navigate('/compositions');
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <AdaptiveModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Composition Updated!' : 'Composition Created!'}
      position="center"
      size="sm"
    >
      <div className="flex items-center gap-3 mb-4">
        <CheckCircle className="h-6 w-6 text-green-600" />
        <h2 className="text-xl font-semibold">
          {isEditing ? 'Composition Updated!' : 'Composition Created!'}
        </h2>
      </div>
      <p className="text-muted-foreground mb-6 text-center">
        <span className="font-medium">"{compositionTitle}"</span> has been {isEditing ? 'updated' : 'saved'} successfully.
      </p>
      
      <div className="flex flex-col gap-3">
        <Button onClick={handleViewComposition} className="w-full">
          <Eye className="mr-2 h-4 w-4" />
          View Composition
        </Button>
        <Button variant="outline" onClick={handleBrowseCompositions} className="w-full">
          <Library className="mr-2 h-4 w-4" />
          Browse All Compositions
        </Button>
      </div>
    </AdaptiveModal>
  );
};

export default CompositionSuccessDialog;